// src/hooks/useFilteredForecastData.ts
import { useMemo } from 'react';
import {
  RebatePoint,
  RebateBreakdown,
  UnifiedForecastResult,
} from '@erp/shared-types';

export function useFilteredForecastData({
  forecastData,
  filterFns,
}: {
  forecastData?: UnifiedForecastResult;
  filterFns: {
    rule?: (bd: RebateBreakdown) => boolean;
    ruleTag?: (bd: RebateBreakdown) => boolean;
    agreement?: (bd: RebateBreakdown) => boolean;
    agreementTag?: (bd: RebateBreakdown) => boolean;
    division?: (bd: RebateBreakdown) => boolean;
    supplier?: (bd: RebateBreakdown) => boolean;
  };
}) {
  return useMemo(() => {
    if (!forecastData) return forecastData;

    const filterBreakdown = (bd: RebateBreakdown) =>
      Object.values(filterFns).every((fn) => !fn || fn(bd));

    const filterRebatePoints = (points: RebatePoint[]) =>
      points.map((point) => {
        const originalHadBreakdowns =
          point.breakdowns && point.breakdowns.length > 0;
        const filteredBreakdowns =
          point.breakdowns?.filter(filterBreakdown) || [];
        const hasFilteredBreakdowns = filteredBreakdowns.length > 0;

        // If point originally had breakdowns but all were filtered out, set totals to 0
        // If point had no breakdowns originally (zero-filled), preserve original totals
        // If point has filtered breakdowns, recalculate from them
        return {
          ...point,
          breakdowns: filteredBreakdowns,
          totalRebateAmount: hasFilteredBreakdowns
            ? filteredBreakdowns.reduce(
                (sum, bd) => sum + (bd.rebateAmount || 0),
                0,
              )
            : originalHadBreakdowns
              ? 0
              : point.totalRebateAmount,
          totalCumulativeRebateAmount: hasFilteredBreakdowns
            ? filteredBreakdowns.reduce(
                (sum, bd) => sum + (bd.cumulativeRebateAmount || 0),
                0,
              )
            : originalHadBreakdowns
              ? 0
              : point.totalCumulativeRebateAmount,
          // Also recalculate volume totals for filtered breakdowns
          totalVolume: hasFilteredBreakdowns
            ? filteredBreakdowns.reduce(
                (sum, bd) => sum + (bd.totalVolume || 0),
                0,
              )
            : originalHadBreakdowns
              ? 0
              : point.totalVolume,
          totalAffectedVolume: hasFilteredBreakdowns
            ? filteredBreakdowns.reduce(
                (sum, bd) => sum + (bd.affectedVolume || 0),
                0,
              )
            : originalHadBreakdowns
              ? 0
              : point.totalAffectedVolume,
        };
      });

    const filteredEstimates = filterRebatePoints(forecastData.estimates || []);
    const filteredPredictions = Object.fromEntries(
      Object.entries(forecastData.predictions || {}).map(([method, points]) => [
        method,
        filterRebatePoints(points || []),
      ]),
    );
    const filteredReported = filterRebatePoints(forecastData.reported || []);

    // Total volume is not rule-attributable — apply only supplier / division /
    // agreement filters so a rule-tag filter doesn't wipe the series.
    const filterTotalVolumeBreakdown = (bd: RebateBreakdown) => {
      if (filterFns.supplier && !filterFns.supplier(bd)) return false;
      if (filterFns.division && !filterFns.division(bd)) return false;
      if (filterFns.agreement && !filterFns.agreement(bd)) return false;
      if (filterFns.agreementTag && !filterFns.agreementTag(bd)) return false;
      return true;
    };
    const filteredTotalVolumeSeries = (
      forecastData.totalVolumeSeries || []
    ).map((point) => {
      const originalHadBreakdowns =
        point.breakdowns && point.breakdowns.length > 0;
      const filteredBreakdowns =
        point.breakdowns?.filter(filterTotalVolumeBreakdown) || [];
      const hasFilteredBreakdowns = filteredBreakdowns.length > 0;
      return {
        ...point,
        breakdowns: filteredBreakdowns,
        totalVolume: hasFilteredBreakdowns
          ? filteredBreakdowns.reduce(
              (sum, bd) => sum + (bd.totalVolume || 0),
              0,
            )
          : originalHadBreakdowns
            ? 0
            : point.totalVolume,
      };
    });

    // Always preserve the backend's overallMetrics (which uses invoice volumes, not n-counted breakdown volumes)
    // These metrics are labeled as "Not Affected by Filters" in the UI and should remain constant
    // regardless of any filtering applied to the forecast data
    const overallMetrics = forecastData.overallMetrics;

    return {
      ...forecastData,
      estimates: filteredEstimates,
      predictions: filteredPredictions,
      reported: filteredReported,
      totalVolumeSeries: filteredTotalVolumeSeries,
      overallMetrics,
    };
  }, [forecastData, filterFns]);
}
